import { debounce } from "@dopplexr/utils/lib/debounce";
import { configureSingle } from "@zenfs/core";
import {
	exists,
	mkdir,
	readdir,
	readFile,
	rm,
	unlink,
	writeFile,
} from "@zenfs/core/promises";
import { WebAccess } from "@zenfs/dom";
import type { Buffer } from "buffer";
import type { WorkspaceInfo } from "@/stores/workspaces";
import { giteaKy, type TreeEntry } from "./gitea";

if (!!Object.keys(localStorage).find((res) => res.startsWith("repos/"))) {
	const root = await navigator.storage.getDirectory();
	for await (const name of root.keys()) {
		await root.removeEntry(name, { recursive: true });
	}
	localStorage.clear();
}

await configureSingle({
	backend: WebAccess,
	handle: await navigator.storage.getDirectory(),
	disableAsyncCache: true,
});

await mkdir("blobs", { recursive: true });
await mkdir("cache/commits", { recursive: true });
await mkdir("cache/metadata", { recursive: true });

const toHexString = (buffer: ArrayBufferLike) =>
	Array.from(new Uint8Array(buffer))
		.map((b) => b.toString(16).padStart(2, "0"))
		.join("");

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export async function generateLFSPointer(buffer: Buffer<ArrayBufferLike>) {
	const sha256Hash = await crypto.subtle
		.digest("SHA-256", new Uint8Array(buffer))
		.then((res) => toHexString(res));

	return `version https://git-lfs.github.com/spec/v1\noid sha256:${sha256Hash}\nsize ${buffer.byteLength}\n`;
}

export async function generateGitHash(content: string) {
	const pointerGitBuffer = textEncoder.encode(
		`blob ${content.length}\0${content}`,
	);

	return crypto.subtle
		.digest("SHA-1", pointerGitBuffer)
		.then((res) => toHexString(res));
}

export function readFileCached(
	uri: string,
	options: {
		encoding: "utf8";
		tree: TreeEntry[];
		ref?: string;
		workspacePath: string;
		offline?: boolean;
	},
): Promise<string>;

export function readFileCached(
	uri: string,
	options: {
		encoding?: null;
		tree: TreeEntry[];
		ref?: string;
		workspacePath: string;
		offline?: boolean;
	},
): Promise<Buffer<ArrayBufferLike>>;

export async function readFileCached(
	encodedUri: string,
	{
		encoding = null,
		tree,
		ref = "main",
		workspacePath,
		offline = false,
	}: {
		encoding?: "utf8" | null;
		tree: TreeEntry[];
		ref?: string;
		workspacePath: string;
		offline?: boolean;
	},
) {
	const uri = decodeURIComponent(encodedUri);
	const entry = tree.find((e) => e.path === uri);

	if (!entry) {
		return null;
	}

	const shaPath = `blobs/${entry.sha}`;

	const fullPath = `${workspacePath}/${uri}`;

	if (await exists(shaPath)) {
		return readFile(shaPath, { encoding });
	}

	if (offline) {
		await rm(fullPath).catch(() => {});

		return null;
	}

	if (!entry.url) {
		return null;
	}

	const url = `${workspacePath}/media/${uri}?ref=${ref}`;

	const file = await giteaKy
		.get(url, { cache: "no-cache" })
		.then(async (res) => new Uint8Array(await res.arrayBuffer()));

	await writeFile(shaPath, file);

	await rm(fullPath).catch(() => {});

	return readFile(shaPath, { encoding });
}

// TODO: pull from .gitattributes of the repo
const LFS_FILES = ["png", "hdr", "jpeg", "jpg", "bin"];

export async function addFile(
	uri: string,
	buffer: Buffer<ArrayBufferLike>,
	workspace: WorkspaceInfo,
	type: "blob" | "tree" = "blob",
) {
	const key = `${type}:::${uri}`;

	const isLFS = LFS_FILES.includes(uri.split(".").pop() ?? "");

	const arrayBuffer = new Uint8Array(buffer);

	const gitHash = await generateGitHash(
		isLFS ? await generateLFSPointer(buffer) : textDecoder.decode(arrayBuffer),
	);

	const fullPath = `${workspace.path}/${uri}`;

	if (type === "blob") {
		const shaPath = `blobs/${gitHash}`;

		if (!(await exists(shaPath))) {
			await writeFile(shaPath, arrayBuffer);
		}

		const parentPath = fullPath.split("/").slice(0, -1).join("/");
		await mkdir(parentPath, { recursive: true });

		await rm(fullPath).catch(() => {});
	} else {
		mkdir(fullPath, { recursive: true });
	}

	const existingEntry = workspace.treeModifications.additions[key];

	if (existingEntry) {
		existingEntry.sha = gitHash;
		existingEntry.lastModified = new Date().toString();
	} else {
		workspace.treeModifications.additions[key] = {
			path: uri,
			type,
			sha: gitHash,
			lastModified: new Date().toString(),
			size: buffer?.byteLength ?? 0,
			local: "ADDED",
		};
	}

	delete workspace.treeModifications.deletions[key];

	workspace.lastModified = Date.now();

	await writeFile(
		`${workspace.path.replace("repos", "cache/metadata")}/meta.json`,
		JSON.stringify(workspace),
	);

	cleanupDebounced();
}

export async function updateFile(
	uri: string,
	buffer: Buffer<ArrayBufferLike>,
	workspace: WorkspaceInfo,
) {
	const key = `blob:::${uri}`;

	const isLFS = LFS_FILES.includes(uri.split(".").pop() ?? "");

	const arrayBuffer = new Uint8Array(buffer);

	const gitHash = await generateGitHash(
		isLFS ? await generateLFSPointer(buffer) : textDecoder.decode(arrayBuffer),
	);

	const shaPath = `blobs/${gitHash}`;

	const fullPath = `${workspace.path}/${uri}`;

	if (!(await exists(shaPath))) {
		await writeFile(shaPath, new Uint8Array(buffer));
	}

	await rm(fullPath).catch(() => {});

	const existingEntry =
		workspace.treeModifications.updations[key] ??
		workspace.treeModifications.additions[key];

	if (existingEntry) {
		existingEntry.sha = gitHash;
		existingEntry.lastModified = new Date().toString();
	} else {
		workspace.treeModifications.updations[key] = {
			path: uri,
			type: "blob",
			sha: gitHash,
			lastModified: new Date().toString(),
			size: buffer.byteLength,
			local: "UPDATED",
		};
	}

	workspace.lastModified = Date.now();

	await writeFile(
		`${workspace.path.replace("repos", "cache/metadata")}/meta.json`,
		JSON.stringify(workspace),
	);
}

export async function resetFile(uri: string, workspace: WorkspaceInfo) {
	const key = `blob:::${uri}`;

	const fullPath = `${workspace.path}/${key}`;

	const unCommittedEntry = workspace.unCommitedTree.find(
		(e) => e.path === uri && e.type === "blob",
	);

	if (!unCommittedEntry) {
		return;
	}

	delete workspace.treeModifications.additions[key];
	delete workspace.treeModifications.updations[key];

	await unlink(fullPath).catch(() => {});

	workspace.lastModified = Date.now();

	await writeFile(
		`${workspace.path.replace("repos", "cache/metadata")}/meta.json`,
		JSON.stringify(workspace),
	);

	cleanupDebounced();
}

export async function removeFile(
	uri: string,
	workspace: WorkspaceInfo,
	type: "tree" | "blob" = "blob",
) {
	const key = `${type}:::${uri}`;

	const fullPath = `${workspace.path}/${key}`;

	const unCommittedEntry = workspace.unCommitedTree.find(
		(e) => e.path === uri && e.type === type,
	);

	if (!unCommittedEntry) {
		return;
	}

	{
		const existingEntry = workspace.treeModifications.additions[key];

		if (existingEntry) {
			delete workspace.treeModifications.additions[key];
		} else {
			workspace.treeModifications.deletions[key] = {
				...unCommittedEntry,
				lastModified: new Date().toString(),
				local: "REMOVED",
			};
		}
	}

	{
		const existingEntry = workspace.treeModifications.updations[key];

		if (existingEntry) {
			delete workspace.treeModifications.updations[key];
		} else {
			workspace.treeModifications.deletions[key] = {
				...unCommittedEntry,
				lastModified: new Date().toString(),
				local: "REMOVED",
			};
		}
	}

	if (type === "blob") {
		await unlink(fullPath).catch(() => {});
	} else {
		await rm(fullPath, { recursive: true, force: true }).catch(() => {});
	}

	workspace.lastModified = Date.now();

	await writeFile(
		`${workspace.path.replace("repos", "cache/metadata")}/meta.json`,
		JSON.stringify(workspace),
	);

	cleanupDebounced();
}

async function cleanup() {
	const metadataDir = await readdir("cache/metadata", { recursive: true });

	const blobs = new Set();
	blobs.add("null");
	for (const subPath of metadataDir) {
		if (!subPath.endsWith("meta.json")) {
			continue;
		}

		const {
			tree,
			treeModifications: { additions, updations },
		}: WorkspaceInfo = await readFile(`cache/metadata/${subPath}`, {
			encoding: "utf8",
		})
			.then(JSON.parse)
			.catch((err) => {
				console.warn(err);
				return {
					tree: [],
					treeModifications: { additions: [], updations: [] },
				};
			});

		for (const { sha } of tree) {
			blobs.add(sha);
		}

		for (const { sha } of Object.values(additions)) {
			blobs.add(sha);
		}

		for (const { sha } of Object.values(updations)) {
			blobs.add(sha);
		}
	}

	const blobDir = await readdir("blobs");

	for (const blob of blobDir) {
		if (!blobs.has(blob)) {
			console.debug(`Cleaning up blobs/${blob}`);
			await rm(`blobs/${blob}`).catch(() => {});
		}
	}
}

export const cleanupDebounced = debounce(cleanup, 120000);
